/* eslint-disable @typescript-eslint/no-empty-function */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { PropertyListener } from '@vsolv/dev-kit/rx';
import { ToastService } from '@vsolv/vectors-ui/alert';
import { Asset } from '@wsphere/assets/domain';
import { Provisioning } from '@wsphere/warranties/domain';
import { BehaviorSubject, combineLatest, switchMap, tap } from 'rxjs';
import { EditAssetDialog } from '../../dialogs';
import { AssetService } from '../../services';
import { AssetDetailsCardComponent } from '../asset-details-card/asset-details-card.component';
@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'ws-asset-picker',
  templateUrl: './asset-picker.component.html',
  standalone: false,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: AssetPickerComponent,
    },
  ],
})
export class AssetPickerComponent implements ControlValueAccessor {
  constructor(
    private assetSvc: AssetService,
    private toastSvc: ToastService
  ) {}
  @PropertyListener('session') session$ = new BehaviorSubject<Provisioning.ProvisioningSession | null>(null);
  @Input() session: Provisioning.ProvisioningSession | null = null;

  @Input() staffView = false;

  @Output() valueChanges = new EventEmitter();
  @Output() assetsReload = new EventEmitter<void>();

  @PropertyListener('assets') assetsInput$ = new BehaviorSubject<Asset.Model[] | null>(null);
  @Input() assets: Asset.Model[] | null = null;

  @PropertyListener('selected') selected$ = new BehaviorSubject<Asset.Model | null>(null);
  selected: Asset.Model | null = null;

  touched = false;
  disabled = false;

  private hiddenAssetIds = new Set<string>();

  totalAssets = 0;

  assets$ = combineLatest([this.session$, this.assetsInput$]).pipe(
    switchMap(async ([session, assetsInput]) => {
      if (assetsInput) {
        return assetsInput.filter(asset => asset.submissions?.length && !this.hiddenAssetIds.has(asset.id));
      }

      if (!session?.customerId) return [];
      const assets = await this.assetSvc.getCustomerAssets(session.customerId, session?.policy?.propertySetId);
      return assets.filter(asset => asset.submissions?.length && !this.hiddenAssetIds.has(asset.id));
    }),
    tap(assets => {
      this.totalAssets = assets.length;
    })
  );

  onChange = (_selected: Asset.Model | null) => {};

  onTouched = () => {};

  selectAsset(asset: Asset.Model) {
    this.onTouched(); // <-- mark as touched
    this.selected = asset;
    this.onChange(this.selected); // <-- call function to let know of a change
    this.valueChanges.emit(asset);
  }
  writeValue(asset: Asset.Model | null): void {
    this.selected = asset;
  }
  registerOnChange(fn: (_selected: Asset.Model | null) => void): void {
    this.onChange = fn; // <-- save the function
  }
  registerOnTouched(fn: any): void {
    this.onTouched = fn; // <-- save the function
  }
  setDisabledState(isDisabled: boolean) {
    this.disabled = isDisabled;
  }

  @ViewChild(EditAssetDialog) editAssetDialog?: EditAssetDialog;
  async openEditDialog(asset: Asset.Model) {
    try {
      await this.editAssetDialog?.open(asset, this.session?.id ?? undefined, this.staffView);
    } catch (err) {
      // ignore - dialog handles its own errors
    }
  }

  onEditClosed(assetId?: string) {
    if (assetId) {
      AssetDetailsCardComponent.clearSubmissionCache(assetId);
    } else {
      AssetDetailsCardComponent.clearSubmissionCache();
    }
    this.assetsReload.emit();

    // Fallback for call-sites that don't provide [assets] from parent.
    if (!this.assets) {
      this.session$.next(this.session);
    }
  }

  private clearSelectedAsset() {
    this.selected = null;
    this.onChange(null);
    this.valueChanges.emit(null);
  }

  private isConflictDeleteError(err: unknown): boolean {
    const httpError = err as HttpErrorResponse & { error?: { statusCode?: number; message?: string } };
    const status = httpError?.status ?? httpError?.error?.statusCode;
    const message = `${httpError?.message || ''} ${httpError?.error?.message || ''}`.toLowerCase();

    return status === 409 || message.includes('409 conflict') || message.includes('unable to delete asset');
  }

  async onDeleted(assetId: string) {
    // Soft-remove from the current checkout list immediately for a smoother UX.
    this.hiddenAssetIds.add(assetId);
    if (this.selected?.id === assetId) {
      this.clearSelectedAsset();
    }
    this.session$.next(this.session);

    try {
      await this.assetSvc.deleteAsset(assetId);
      this.assetsReload.emit();
      this.toastSvc.show({ type: 'success', title: 'Asset deleted', text: 'Asset was successfully deleted.' });
    } catch (err) {
      const httpError = err as HttpErrorResponse;
      if (this.isConflictDeleteError(err)) {
        this.toastSvc.show({
          type: 'warning',
          title: 'Asset linked to active records',
          text: 'This asset cannot be deleted, so it was removed from this checkout list.',
        });
        return;
      }

      // Restore asset visibility if the failure was not the expected linkage conflict.
      this.hiddenAssetIds.delete(assetId);
      this.session$.next(this.session);
      this.assetsReload.emit();

      this.toastSvc.show({
        type: 'error',
        title: 'Unable to delete asset',
        text: httpError?.error?.message || httpError?.message || 'Failed to delete the asset. Please try again.',
      });
    }
  }
}
